import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Trash2, Baby, UserRound } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const COLORS = ["from-brand to-red-700", "from-pink to-fuchsia-700", "from-gold to-amber-600", "from-sky-500 to-indigo-700"];

export default function ProfilePicker() {
  const nav = useNavigate();
  const { setActiveProfile } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [max, setMax] = useState(3);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [isKids, setIsKids] = useState(false);

  const load = () => api.get("/profiles").then(({ data }) => { setProfiles(data.profiles); setMax(data.max); }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const pick = (p) => { setActiveProfile(p); nav("/"); };

  const create = async () => {
    if (!name.trim()) return;
    try {
      await api.post("/profiles", { name: name.trim(), is_kids: isKids });
      setName(""); setIsKids(false); setAdding(false); load();
      toast.success("Profile added");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add profile"); }
  };

  const remove = async (pid) => {
    try { await api.delete(`/profiles/${pid}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Cannot delete"); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-brand" size={40} /></div>;

  return (
    <div className="min-h-screen grid place-items-center px-4" data-testid="profile-picker-page">
      <div className="text-center w-full max-w-2xl">
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold mb-2">Who's watching?</h1>
        <p className="text-zinc-400 mb-10">Up to {max} profiles per subscription · stream on {max} screens.</p>
        <div className="flex flex-wrap justify-center gap-6">
          {profiles.map((p, i) => (
            <div key={p.id} className="group relative">
              <button data-testid={`profile-${p.id}`} onClick={() => pick(p)} className="flex flex-col items-center gap-3">
                <div className={`w-28 h-28 rounded-2xl bg-gradient-to-br ${COLORS[i % COLORS.length]} grid place-items-center text-4xl font-extrabold group-hover:ring-4 ring-white transition-all`}>
                  {p.is_kids ? <Baby size={44} /> : (p.name?.[0]?.toUpperCase() || "U")}
                </div>
                <span className="text-zinc-300 group-hover:text-white flex items-center gap-1">{p.name}{p.is_kids && <span className="text-[10px] bg-pink px-1.5 rounded">KIDS</span>}</span>
              </button>
              {profiles.length > 1 && (
                <button onClick={() => remove(p.id)} title="Delete" className="absolute -top-2 -right-2 w-7 h-7 grid place-items-center rounded-full bg-black/70 border border-white/20 opacity-0 group-hover:opacity-100 transition">
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
          {profiles.length < max && (
            adding ? (
              <div className="w-56 bg-card border border-white/10 rounded-2xl p-4 text-left">
                <input data-testid="new-profile-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Profile name"
                  className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 mb-3 outline-none focus:border-brand" />
                <label className="flex items-center gap-2 text-sm text-zinc-300 mb-3 cursor-pointer">
                  <input type="checkbox" checked={isKids} onChange={(e) => setIsKids(e.target.checked)} /> Kids profile
                </label>
                <div className="flex gap-2">
                  <button data-testid="save-profile-btn" onClick={create} className="flex-1 bg-brand py-2 rounded-lg text-sm font-semibold">Save</button>
                  <button onClick={() => setAdding(false)} className="px-3 bg-white/10 rounded-lg text-sm">Cancel</button>
                </div>
              </div>
            ) : (
              <button data-testid="add-profile-btn" onClick={() => setAdding(true)} className="flex flex-col items-center gap-3">
                <div className="w-28 h-28 rounded-2xl border-2 border-dashed border-white/20 grid place-items-center text-zinc-500 hover:border-white hover:text-white transition"><Plus size={40} /></div>
                <span className="text-zinc-500">Add Profile</span>
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
